import React from 'react'
import {
  Box,
  BoxProps,
  Container,
  Stack,
  Title,
  StarRating,
  LinkButton,
  Text,
  useCRUDQuery,
  Button,
} from '@thirstycamel/ui'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import { ReviewEntity } from '@ts/core/src/modules/review/review.entity'

import ProductReviewCard from '../ProductReviewCard'
import { useUser } from '../../hooks/useUser'
import { useActions } from '../../store/hooks'

export interface ProductReviewsProps extends BoxProps {
  product: CoreProductEntity
  isFetching?: boolean
}

export const ProductReviews = ({ product, isFetching, ...restProps }: ProductReviewsProps) => {
  const { isLoggedIn } = useUser()
  const { openLoginPopout } = useActions(store => store.auth)
  const query = RequestQueryBuilder.create({
    join: { field: 'productGroup' },
    filter: [
      { field: 'productGroup.id', operator: 'eq', value: product?.productGroup?.id },
      { field: 'published', operator: 'eq', value: true },
    ],
  })
    .sortBy({ field: 'createdAt', order: 'DESC' })
    .query()

  const reviews = useCRUDQuery<ReviewEntity>(product && [`/reviews?${query}`])

  const items = reviews.latestData?.data

  return (
    <Box bg="gray.50" borderTop="1px" borderColor="gray.200" {...restProps}>
      <Container size="xs" py={[12, , 16]}>
        <Stack spacing={10}>
          <Stack spacing={5} align="center">
            <Stack>
              <Title textAlign="center">Reviews</Title>

              <Stack spacing={2} isInline alignItems="center">
                <StarRating value={product?.productGroup?.rating || 0} size="xl" />

                <Text fontWeight="bold" color="blackAlpha.600" fontSize="sm">
                  ({product?.productGroup?.numberOfReviews || 0})
                </Text>
              </Stack>
            </Stack>

            {isLoggedIn ? (
              <LinkButton
                href="/product/[group]/[slug]/review"
                hrefAs={`/product/${product?.group}/${product?.slug}/review`}
                variant="outline"
                variantColor="pink"
              >
                Write a review
              </LinkButton>
            ) : (
              <>
                <Button
                  display={['none', , 'flex']}
                  onClick={() => openLoginPopout()}
                  variant="outline"
                  variantColor="pink"
                >
                  Login and write a review
                </Button>
                <LinkButton
                  display={['flex', , 'none']}
                  href={`/login?redirectTo=/product/${product?.group}/${product?.slug}/review`}
                  hrefAs={`/login?redirectTo=/product/${product?.group}/${product?.slug}/review`}
                  variant="outline"
                  variantColor="pink"
                >
                  Write a review
                </LinkButton>
              </>
            )}
          </Stack>

          <Stack spacing={5}>
            {items?.map(review => (
              <ProductReviewCard review={review} />
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

export default ProductReviews
